import { memo } from 'react';
import { useHasSinglePurchasableTier, useSubscription } from '@features/subscriptions';
import type { SxProps, Theme } from '@mui/material';

import { EntitlementUsageCard } from '../components/EntitlementUsageCard';
import { useEntitlementUpgradeAction } from '../hooks/useEntitlementUpgradeAction';
import { useEntitlement } from '../store/api';

/**
 * Capabilities that should show EntitlementUsageCard even with single-tier catalog.
 * These are features where showing usage/limits is valuable independent of upgrades.
 */
const ALWAYS_SHOW_CAPABILITIES = new Set(['audit_suggestions', 'ehr_note_copy']);

export interface EntitlementUsageProps {
  /** The capability name to display (e.g., 'notes', 'labs') */
  capabilityName: string;

  /** Source for analytics tracking (e.g., 'notes_list', 'encounter_create') */
  source?: string;

  /** Pass-through props for EntitlementUsageCard */
  showUpgradeLink?: boolean;
  variant?: 'compact' | 'standard';
  noBorderRadius?: boolean;

  /** Optional override for the upgrade click handler */
  onUpgradeClick?: () => void;

  /** Optional sx props for wrapper styling */
  sx?: SxProps<Theme>;
}

/**
 * EntitlementUsage Component
 *
 * Container/orchestrator component that:
 * - Fetches user entitlements from dedicated entitlements endpoint
 * - Finds the entitlement matching the provided capability name
 * - Renders EntitlementUsageCard with the found entitlement
 * - Shows upgrade link for all users (admins navigate, non-admins see contact admin modal)
 *
 * Returns null if:
 * - Entitlement is not found
 * - Entitlements are still loading (shows loading state via EntitlementUsageCard)
 * - Single-tier catalog AND capability is not in allowlist (e.g., audit_suggestions)
 *
 * @example
 * ```tsx
 * <EntitlementUsage capabilityName="note_creation" />
 * ```
 */
export const EntitlementUsage = memo<EntitlementUsageProps>(
  ({
    capabilityName,
    source,
    showUpgradeLink = true,
    variant = 'standard',
    noBorderRadius = false,
    onUpgradeClick: onUpgradeClickProp,
    sx,
  }) => {
    // TODO: Re-enable clinician role check after backend entitlement change
    const isClinician = true; // Temporarily default to true

    // Get entitlement using dedicated entitlements endpoint
    // refetchOnMountOrArgChange ensures fresh data when component mounts (e.g., after note generation)
    // refetchOnFocus ensures fresh data when user returns to the browser tab
    // Note: We intentionally only use isLoading (not isFetching) to implement stale-while-revalidate
    // pattern - showing cached data during background refetches instead of loading skeletons
    const { entitlement, isLoading } = useEntitlement(capabilityName, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });
    const { hasSinglePurchasableTier, isLoading: isTierCatalogLoading } =
      useHasSinglePurchasableTier();

    // Shared upgrade handler: solo admin → modal, group admin → clinicians page, non-admin → contact admin
    const { handleUpgradeClick: defaultUpgradeClick } = useEntitlementUpgradeAction({
      capabilityName,
      bundleId: entitlement?.bundle_id,
    });
    const handleUpgradeClick = onUpgradeClickProp ?? defaultUpgradeClick;

    // Get subscription details to check if user is trialing
    const { subscription } = useSubscription();
    const isTrialing = subscription?.subscription_status === 'trialing';

    // Catalog data controls only this single-tier presentation rule. Avoid a
    // flash while that metadata is loading; entitlement availability is still
    // determined solely by the entitlement endpoint above.
    if (
      (isTierCatalogLoading || hasSinglePurchasableTier) &&
      !ALWAYS_SHOW_CAPABILITIES.has(capabilityName)
    ) {
      return null;
    }

    // Return null if entitlement not found (but not while loading)
    if (!isLoading && !entitlement) {
      return null;
    }

    // Render EntitlementUsageCard with found entitlement
    return (
      <EntitlementUsageCard
        entitlement={entitlement}
        onUpgradeClick={handleUpgradeClick}
        showUpgradeLink={showUpgradeLink}
        variant={variant}
        loading={isLoading}
        noBorderRadius={noBorderRadius}
        source={source}
        isTrialing={isTrialing}
        isNonClinician={!isClinician}
        sx={sx}
      />
    );
  },
);

EntitlementUsage.displayName = 'EntitlementUsage';
